import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useFormContext } from "react-hook-form";
import { RHFRadioGroup } from "../../hook-form/RHFRadioGroup";
import { RHFTextField } from "../../hook-form/RHFTextField";
import { RHFDatePicker } from "../../hook-form/RHFDateSelect";
import { RHFCheckbox } from "../../hook-form/RHFCheckbox";
import {
  Box,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  FoilCutterPostPayload,
  FoilType,
} from "../../../types/foilCutterTypes";
import { postMaterialFoil } from "../../../services/materialFoilService";
import { MaterialFoilType } from "../../../types/materialFoilTypes";

type Props = {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onSaved?: (item: MaterialFoilType) => void;
  foilTypes?: FoilType[];
  stationId: number;
};

export function FoilCutterForm({
  onSuccess,
  onError,
  onSaved,
  foilTypes = [],
  stationId,
}: Props) {
  const methods = useFormContext<any>();
  const { control, handleSubmit, reset, setError, watch, setValue, formState } =
    methods;
  const { isSubmitting } = formState;
  const watchFoilType = watch("foilType");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<
    null | (() => Promise<void>)
  >(null);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    setValue("other", watchFoilType === "___OTHER___");
  }, [watchFoilType, setValue]);

  const onSubmit = async (data: {
    foilBatch: string;
    foilExpirationDate: Date;
    isFoilSampleStored: boolean;
    foilType: string;
    otherText?: string;
  }) => {
    if (data.foilExpirationDate) {
      const selectedDate = new Date(data.foilExpirationDate);
      if (!isNaN(selectedDate.getTime())) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        selectedDate.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          setConfirmOpen(true);
          setPendingSubmit(() => async () => {
            await executeSubmit(data);
          });
          return;
        }
      }
    }

    await executeSubmit(data);
  };

  const executeSubmit = async (data: {
    foilBatch: string;
    foilExpirationDate: Date;
    isFoilSampleStored: boolean;
    foilType: string;
    otherText?: string;
  }) => {
    if (savingRef.current) return;
    let displayName = data.foilType;
    if (displayName === "___OTHER___") {
      const trimmed = (data.otherText || "").trim();
      if (!trimmed) {
        setError("otherText", {
          type: "required",
          message: "Bitte geben Sie eine Folienbezeichnung ein.",
        });
        return;
      }
      displayName = trimmed;
    }

    const chosenFoilType = foilTypes.find((f) => f.name === displayName);
    const resolvedStandardFoil =
      (chosenFoilType && chosenFoilType.standardFoil) != null
        ? chosenFoilType!.standardFoil
        : stationId;

    const payload: FoilCutterPostPayload = {
      stationid: stationId,
      dateInProduction: new Date(),
      foilbatch: data.foilBatch,
      foilExpirationDate: data.foilExpirationDate,
      isFoilSampleStored: data.isFoilSampleStored,
      name: displayName,
      standardfoil: resolvedStandardFoil,
    };

    try {
      savingRef.current = true;
      setIsSaving(true);
      const created = await postMaterialFoil(payload);
      reset();
      if (created) {
        onSaved?.(created as MaterialFoilType);
      }
      onSuccess?.();
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "CanceledError") {
        console.debug("FoilCutter request canceled");
        return;
      }
      onError?.(error?.message || "Fehler beim Speichern");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const foilTypeOptions = useMemo(() => {
    const base = foilTypes.map((f) => ({ label: f.name, value: f.name }));
    const filtered = base.filter(
      (f) => (f.label?.toLowerCase() || "") !== "andere"
    );
    filtered.push({ label: "Andere", value: "___OTHER___" });
    return filtered;
  }, [foilTypes]);

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} p={2}>
        <Stack spacing={2}>
          <RHFTextField
            name="foilBatch"
            label="Batch Number"
            control={control}
          />

          <RHFDatePicker
            name="foilExpirationDate"
            label="Expiration Date"
            control={control}
          />

          <RHFCheckbox
            name="isFoilSampleStored"
            label="Rückstellmuster bereit"
            control={control}
          />

          <RHFRadioGroup
            name="foilType"
            label="Foil Type"
            options={foilTypeOptions}
            control={control}
            row
          />

          {watchFoilType === "___OTHER___" && (
            <RHFTextField
              name="otherText"
              label="Benutzerdefinierter Folientyp"
              control={control}
              required
            />
          )}

          <Button
            variant="contained"
            type="submit"
            disabled={isSaving || isSubmitting}
          >
            {isSaving || isSubmitting ? "Speichern…" : "Speichern"}
          </Button>
        </Stack>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Abgelaufenes Ablaufdatum</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Das gewählte Ablaufdatum ist bereits abgelaufen. Möchten Sie den
            Eintrag trotzdem speichern?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            Abbrechen
          </Button>
          <Button
            disabled={isSaving}
            onClick={async () => {
              setConfirmOpen(false);
              const action = pendingSubmit;
              setPendingSubmit(null);
              if (action) await action();
            }}
            color="error"
            variant="contained"
          >
            Trotzdem speichern
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
